import React from 'react';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import Avatar from '@mui/material/Avatar';
import clsx from 'clsx';
import {
  ToDoActivities,
} from 'dtos/toDoActivities';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import type {
  UserMessageDTO,
} from 'dtos';
import ToolTip from 'core/components/Tooltip';
import {
  statusLabels,
} from 'pages/Dashboard/pages/Todos/normalize';
import {
  statusColorClasses,
} from 'pages/Dashboard/pages/Todos/components/StatusSelect';
import {
  useUserProfilePicture,
} from 'pages/Dashboard/hooks/media';

type StatusDotProps = {
  status?: UserMessageDTO['status'];
};

type StatusPillProps = {
  status?: UserMessageDTO['status'];
};

type MessageCellProps = {
  row: {
    original: UserMessageDTO;
  };
};

type StatusClasses = {
  accent: string;
  avatar: string;
  pill: string;
};

type MessageStatus = NonNullable<UserMessageDTO['status']>;

const defaultStatusClasses: StatusClasses = {
  accent: 'bg-sky-400',
  avatar: '!bg-blue-100 !text-blue-700',
  pill: 'bg-sky-100 text-sky-700',
};

const statusClassesByStatus: Partial<Record<MessageStatus, StatusClasses>> = {
  Completed: {
    accent: 'bg-emerald-400',
    avatar: '!bg-emerald-100 !text-emerald-700',
    pill: 'bg-emerald-100 text-emerald-700',
  },
  InProgress: {
    accent: 'bg-violet-400',
    avatar: '!bg-violet-100 !text-violet-700',
    pill: 'bg-violet-100 text-violet-700',
  },
  Abandoned: {
    accent: 'bg-gray-400',
    avatar: '!bg-gray-200 !text-gray-700',
    pill: 'bg-gray-200 text-gray-700',
  },
  NotStarted: defaultStatusClasses,
};

type ToDoMessageParts = {
  leadingText: string;
  action: string;
  trailingText: string;
};

function getToDoMessageParts(
  activityType: UserMessageDTO['activityType'],
  actorName?: string | null,
): ToDoMessageParts {
  const displayActorName = actorName?.trim() || 'Someone';

  switch (activityType) {
  case ToDoActivities.CommentTagged:
    return {
      leadingText: `${displayActorName} `,
      action: 'mentioned',
      trailingText: ' you in a comment',
    };
  case ToDoActivities.ToDoAssigned:
    return {
      leadingText: `${displayActorName} `,
      action: 'assigned',
      trailingText: ' you to a to-do',
    };
  case ToDoActivities.ToDoUnassigned:
    return {
      leadingText: `${displayActorName} `,
      action: 'unassigned',
      trailingText: ' you from a to-do',
    };
  case ToDoActivities.Completed:
    return {
      leadingText: `${displayActorName} marked a to-do as `,
      action: 'completed',
      trailingText: '',
    };
  case ToDoActivities.Abandoned:
    return {
      leadingText: `${displayActorName} marked a to-do as `,
      action: 'abandoned',
      trailingText: '',
    };
  default:
    return {
      leadingText: `${displayActorName} `,
      action: 'updated',
      trailingText: ' a to-do',
    };
  }
}

function MessageText({
  activityType,
  actorName,
}: Pick<UserMessageDTO, 'activityType' | 'actorName'>) {
  const {
    leadingText,
    action,
    trailingText,
  } = getToDoMessageParts(activityType, actorName);

  return (
    <>
      {leadingText}
      <strong className="font-semibold text-gray-800">{action}</strong>
      {trailingText}
    </>
  );
}

function getAvatarText(messageText: string) {
  const words = messageText
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (words.length === 0) {
    return 'U';
  }

  return words.map((word) => word[0]?.toUpperCase() ?? '').join('');
}

function StatusDot({ status }: StatusDotProps) {
  const statusLabel = status ? statusLabels[status] ?? status : null;

  return !statusLabel ? null : (
    <ToolTip
      title={statusLabel}
      placement="top"
    >
      <span
        className={clsx(
          'h-2.5 w-2.5 shrink-0 rounded-full',
          status ? statusColorClasses[status] : '!bg-gray-100',
        )}
      />
    </ToolTip>
  );
}

function getStatusClasses(status?: MessageStatus) {
  return statusClassesByStatus[status ?? 'NotStarted'] ?? defaultStatusClasses;
}

function StatusPill({ status }: StatusPillProps) {
  const statusLabel = status ? statusLabels[status] ?? status : null;

  return !statusLabel ? null : (
    <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-semibold', getStatusClasses(status).pill)}>
      {statusLabel}
    </span>
  );
}

function MessageCell({ row }: MessageCellProps) {
  const {
    activityType,
    actorName,
    entityName,
    status,
    actorUserId,
  } = row.original;
  const avatarText = getAvatarText(actorName?.trim() || 'User');
  const statusClasses = getStatusClasses(status);
  const { src: profilePictureUrl } = useUserProfilePicture(actorUserId ?? undefined);

  return (
    <div className="relative flex h-full min-w-0 items-center gap-3 py-3 pl-4 pr-2">
      <div className={clsx('absolute inset-y-0 left-0 w-1 shrink-0', statusClasses.accent)} />
      <Avatar
        src={profilePictureUrl ?? undefined}
        className={clsx('!h-9 !w-9 !text-sm !font-semibold', statusClasses.avatar)}
      >
        {avatarText}
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 overflow-hidden py-1">
        <div className="flex min-w-0 items-center overflow-hidden">
          <span className="truncate text-xs font-medium text-gray-600">
            <MessageText activityType={activityType} actorName={actorName} />
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-2 overflow-hidden">
          <StatusDot status={status} />
          <span className="truncate text-sm font-semibold text-gray-900">
            {entityName || '--'}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-2 overflow-hidden">
          <StatusPill status={status} />
        </div>
      </div>
    </div>
  );
}

const columns: ColumnDef<UserMessageDTO>[] = [{
  id: 'message',
  header: '',
  minSize: 720,
  meta: { autoSized: true },
  cell: ({ row }) => <MessageCell row={row} />,
}, {
  id: 'timeAgo',
  header: '',
  size: 150,
  cell: ({ row }) => (
    <div className="flex h-full items-center justify-end gap-2 py-1 pr-2 pl-2">
      <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-gray-400">
        <AccessTimeRoundedIcon className="!h-3.5 !w-3.5" />
        {row.original.timeAgo || '--'}
      </span>
    </div>
  ),
}];

export default columns;
